import { useCallback } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import {
  Button,
  ButtonIconWithLabel,
  HeaderContainer,
  Icon_Download24,
  isLoggedIn,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import { ShareModalContextProvider } from "../../../contexts/ShareModalFormContext";
import useAccount from "../../../hooks/useAccount";
import { useProjectViewLinkSharing } from "../../../hooks/useProjectViewLink";
import { ElementClickAction } from "../../../lib/TrackingAdapter";
import useTracker from "../../../utils/useTracker";
import { LoginButton } from "../components/LoginButton/LoginButton";
import { LogoHeader } from "../components/LogoHeader/LogoHeader";
import { MenuHeader } from "../components/MenuHeader/MenuHeader";
import QRCodeFlyout from "../components/QRCodeFlyout/QRCodeFlyout";
import { ShareViewLinkModal } from "../components/ShareModal/ShareViewLinkModal";
import { StartModelingButton } from "../components/StartModelingButton/StartModelingButton";

import style from "./ModelViewerHeader.module.css";

interface ModelViewerHeaderProps {
  disableARButton?: boolean;
  hideARButton?: boolean;
  title?: string;
  isDrawingsLayout?: boolean;
  modelViewer?: React.RefObject<ModelViewerElement>;
  downloadPdf?: (drawingId: string) => void;
  teamId?: string;
}

const ModelViewerHeader: React.FC<ModelViewerHeaderProps> = ({
  disableARButton,
  hideARButton,
  title,
  isDrawingsLayout = false,
  modelViewer,
  downloadPdf = () => null,
  teamId,
}) => {
  const tracker = useTracker();

  const { drawingId } = useParams();
  const { data: accountData, isPending: isAccountPending } = useAccount();
  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();

  const onDownloadPdfButtonclick = useCallback(() => {
    tracker.trackButtonClick({
      action: ElementClickAction.DOWNLOAD_PDF,
      elementId: "download-pdf-button",
    });

    if (drawingId && isDrawingsLayout) {
      downloadPdf(drawingId);
    }
  }, [drawingId, downloadPdf, isDrawingsLayout, tracker]);

  return (
    <HeaderContainer>
      <LogoHeader teamId={teamId} />
      {title && (
        <>
          <div className={classNames(style.separator, style.logoSeparator)} />
          <div className={classNames(style.title)}>{title}</div>
        </>
      )}
      <div className={style.buttonContainer}>
        <StartModelingButton />
        {!hideARButton && (
          <div className={style.arButtonContainer}>
            {drawingId && isDrawingsLayout && (
              <Button
                variant="grayscale"
                onClick={onDownloadPdfButtonclick}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
              >
                <ButtonIconWithLabel icon={<Icon_Download24 />} label="PDF" />
              </Button>
            )}

            {!(drawingId && isDrawingsLayout) && (
              <QRCodeFlyout modelViewer={modelViewer} disabled={disableARButton} />
            )}
            {projectViewLinkSharingData && (
              <ShareModalContextProvider>
                <ShareViewLinkModal />
              </ShareModalContextProvider>
            )}
          </div>
        )}
        {isLoggedIn() && !isAccountPending && <MenuHeader accountData={accountData} />}
        {!isLoggedIn() && (
          <>
            <div className={style.separator} />
            <LoginButton />
          </>
        )}
      </div>
    </HeaderContainer>
  );
};

export default ModelViewerHeader;
